import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const TeamMembersPanel = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const { getAuthHeaders } = useAuth();
  
  const emptyForm = {
    name: '',
    position: '',
    bio: '',
    photo: '',
    email: '',
    linkedin: '',
    order: 0,
    is_leadership: true
  };
  
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/team-members`);
      const data = await response.json();
      setMembers(data);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData(emptyForm);
    setIsAdding(true);
    setEditingId(null);
  };

  const handleEdit = (member) => {
    setFormData(member);
    setEditingId(member.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setIsAdding(false);
  };

  const handleSave = async () => {
    try {
      const url = editingId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cms/team-members/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cms/team-members`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success(editingId ? 'Member updated!' : 'Member added!');
      loadMembers();
      handleCancel();
    } catch (error) {
      toast.error('Failed to save member');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/team-members/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Member deleted!');
      loadMembers();
    } catch (error) {
      toast.error('Failed to delete member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#320547]" size={48} />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Team Members</CardTitle>
          {!isAdding && !editingId && (
            <Button
              onClick={handleAdd}
              className="bg-[#320547] hover:bg-[#95228A]"
            >
              <Plus className="mr-2" size={20} />
              Add Member
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {(isAdding || editingId) && (
          <div className="mb-6 p-6 border rounded-2xl bg-white">
            <h3 className="text-lg font-semibold mb-4">
              {editingId ? 'Edit Member' : 'Add New Member'}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Position *</label>
                  <Input
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Bio</label>
                <Textarea
                  value={formData.bio || ''}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Photo URL *</label>
                <Input
                  value={formData.photo}
                  onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">LinkedIn</label>
                  <Input
                    value={formData.linkedin || ''}
                    onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Display Order</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  />
                </div>
                <div className="flex items-center pt-8">
                  <input
                    type="checkbox"
                    checked={formData.is_leadership}
                    onChange={(e) => setFormData({ ...formData, is_leadership: e.target.checked })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium">Leadership Team</label>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button onClick={handleCancel} variant="outline">
                  <X className="mr-2" size={20} />
                  Cancel
                </Button>
                <Button onClick={handleSave} className="bg-[#320547] hover:bg-[#95228A]">
                  <Save className="mr-2" size={20} />
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {members.map((member) => (
            <Card key={member.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="font-bold text-lg">{member.name}</h4>
                    <p className="text-sm text-gray-400">{member.position}</p>
                    {member.bio && (
                      <p className="text-xs text-gray-500 mt-2 line-clamp-2">{member.bio}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => handleEdit(member)}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    <Edit2 size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(member.id)}
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {members.length === 0 && !isAdding && (
          <div className="text-center py-12 text-gray-500">
            No team members yet. Click "Add Member" to get started.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamMembersPanel;